import { gql } from '@apollo/client'
import { Button, Chip, Heading, Subtitle } from '@farol-ds/react'
import { Price } from '@jusbrasil-web/shared-price'
import { useEffect, useState } from 'react'

import { PlanFrequency } from '../../constants'
import { CheckoutButton } from '../checkout-button'
import { BenefitsList } from '../dynamic-multiple-offers-switch-frequency-new-benefits-lock/components/benefits-list'
import { OneClickButton } from '../one-click-button'
import {
  AiOfferLockContent_OfferLockDataFragment,
  AiOfferLockContent_ViewerFragment,
} from './__generated__/ai-offer-lock-content.graphql'
import styles from './ai-offer-lock.module.scss'
import { JusAILogo } from './jus-ai-logo'

export interface AiOfferLockContentProps {
  offerLockData: AiOfferLockContent_OfferLockDataFragment
  viewer?: AiOfferLockContent_ViewerFragment | null
}

export function AiOfferLockContent({ offerLockData, viewer }: AiOfferLockContentProps) {
  const { body, header } = offerLockData || {}
  const [currentUrl, setCurrentUrl] = useState('')
  const { title = '', subtitle = '' } = header || {}
  const {
    cards: [card],
  } = body || {}

  useEffect(() => {
    setCurrentUrl(window.location.href)
  }, [])

  const href = `https://www.jusbrasil.com.br/login?next_url=${encodeURIComponent(currentUrl)}`
  const buttonLabel = card.button.label
  const isYearlyOffer = card?.offer?.frequency === PlanFrequency.YEARLY
  const { label: chipLabel } = card?.chip || {}

  return (
    <>
      <div className={styles['ai-offer-lock-header']}>
        <JusAILogo width={80} height={22} />

        {chipLabel && (
          <Chip kind="secondary" size="sm" className={styles['ai-offer-lock-header-chip']}>
            <span dangerouslySetInnerHTML={{ __html: chipLabel }} />
          </Chip>
        )}

        {title && subtitle && (
          <>
            <Heading size="lg" className={styles['ai-offer-lock-header-title']}>
              <span dangerouslySetInnerHTML={{ __html: title }} />
            </Heading>
            <Subtitle className={styles['ai-offer-lock-header-subtitle']}>
              <span dangerouslySetInnerHTML={{ __html: subtitle }} />
            </Subtitle>
          </>
        )}

        {card.offer.priceText && (
          <Price align="left" size="md">
            {card.offer.priceTitle && (
              <Price.Title className={styles['ai-offer-lock-price-title']}>
                {card.offer.installmentsText ? (
                  <span data-testid="price-label">
                    {card.offer.priceTitle}
                    <strong className={styles['ai-offer-lock-price-installments']}>
                      {' '}
                      {card.offer.installmentsText}
                    </strong>{' '}
                    de:
                  </span>
                ) : (
                  <span dangerouslySetInnerHTML={{ __html: card.offer.priceTitle }} />
                )}{' '}
              </Price.Title>
            )}
            <Price.Value>
              {card.offer.priceText}
              {!isYearlyOffer && card.offer.frequency && (
                <Price.ValueFrequency>{card.offer.frequency}</Price.ValueFrequency>
              )}
            </Price.Value>
          </Price>
        )}
      </div>

      <div className={styles.benefits}>
        <BenefitsList offerLockCard={card} />
      </div>

      <div className={styles['ai-offer-lock-card']}>
        {viewer ? (
          <>
            <CheckoutButton offerLockCard={card} kind="primary" />
            <OneClickButton offerLockCard={card} viewer={viewer} kind="primary" />
          </>
        ) : (
          <>
            <Button kind="primary" asChild>
              <a href={href}>{buttonLabel}</a>
            </Button>
          </>
        )}
      </div>
    </>
  )
}

AiOfferLockContent.fragments = {
  offerLockData: gql`
    fragment AiOfferLockContent_offerLockData on OfferLockData {
      header {
        title
        subtitle
      }
      body {
        cards {
          chip {
            label
          }
          offer {
            priceTitle
            priceText
            installmentsText
            frequency
            priceAfterPromotionalPeriodText
          }
          ...OneClickButton_offerLockCard
          ...CheckoutButton_offerLockCard
          ...NewBenefitsList_offerLockCard
        }
      }
    }
    ${OneClickButton.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
    ${BenefitsList.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment AiOfferLockContent_viewer on Profile {
      ...OneClickButton_viewer
    }
    ${OneClickButton.fragments.viewer}
  `,
}
